import React, { useEffect, useState } from 'react';
import { Clock, Globe, Monitor, TrendingUp } from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { toast } from 'sonner';

import DataTable from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import LoadingSkeleton from '../components/LoadingSkeleton';
import StatCard from '../components/StatCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import api, { analyticsApi } from '../lib/api';

const RANGE_OPTIONS = [
  { key: 'today', label: 'Today', days: 1 },
  { key: '7d', label: '7d', days: 7 },
  { key: '30d', label: '30d', days: 30 },
  { key: '90d', label: '90d', days: 90 },
];

const CHART_COLORS = {
  productive: '#10b981',
  unproductive: '#f43f5e',
  neutral: '#64748b',
  apps: ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ec4899', '#14b8a6'],
  web: ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#f43f5e', '#64748b'],
};

const tooltipStyle = {
  backgroundColor: '#1e293b',
  border: '1px solid #334155',
  borderRadius: '12px',
  color: '#e2e8f0',
};

function getDateWindow(rangeKey) {
  const option = RANGE_OPTIONS.find((range) => range.key === rangeKey) || RANGE_OPTIONS[1];
  const end = new Date();
  end.setHours(23, 59, 59, 999);

  const start = new Date(end);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (option.days - 1));

  return {
    days: option.days,
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

function durationBetween(start, end) {
  const seconds = Math.max(
    0,
    Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000)
  );
  return Number.isFinite(seconds) ? seconds : 0;
}

function formatDuration(seconds) {
  const totalMinutes = Math.round((seconds || 0) / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours && minutes) return `${hours}h ${minutes}m`;
  if (hours) return `${hours}h`;
  return `${minutes}m`;
}

function formatTimestamp(value) {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatDayLabel(value) {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(new Date(value));
}

function classifyApp(appName = '', windowTitle = '') {
  const value = `${appName} ${windowTitle}`.toLowerCase();
  if (/slack|teams|zoom|meet|jira|github|gitlab|vscode|code|intellij|terminal|cursor|copilot|notion|excel|word|sheets|docs/.test(value)) {
    return 'productive';
  }
  if (/youtube|netflix|instagram|facebook|x.com|twitter|tiktok|reddit|spotify|amazon|flipkart|myntra/.test(value)) {
    return 'unproductive';
  }
  return 'neutral';
}

function categorizeDomain(domain = '') {
  const value = domain.toLowerCase();
  if (/github|gitlab|atlassian|jira|notion|docs|drive|slack|teams|zoom|figma|linear/.test(value)) return 'Work';
  if (/linkedin|facebook|instagram|x\.com|twitter|reddit|tiktok|snapchat/.test(value)) return 'Social Media';
  if (/nytimes|cnn|bbc|news|medium|substack/.test(value)) return 'News';
  if (/amazon|ebay|flipkart|myntra|shop|walmart/.test(value)) return 'Shopping';
  if (/youtube|netflix|primevideo|twitch|spotify/.test(value)) return 'Entertainment';
  if (/openai|claude|anthropic|gemini|copilot|perplexity/.test(value)) return 'AI';
  return 'Other';
}

function extractDomain(windowTitle = '') {
  const match = windowTitle.match(/([a-z0-9-]+(?:\.[a-z0-9-]+)+)/i);
  return match ? match[1].toLowerCase() : 'Other';
}

function buildDailyActivity(activityRows = [], days = 7) {
  const series = Array.from({ length: days }).map((_, index) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - (days - index - 1));
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
      date.getDate()
    ).padStart(2, '0')}`;

    return {
      date: key,
      label: formatDayLabel(key),
      productive: 0,
      unproductive: 0,
      neutral: 0,
    };
  });

  const seriesMap = series.reduce((accumulator, item) => {
    accumulator[item.date] = item;
    return accumulator;
  }, {});

  activityRows.forEach((row) => {
    const d = new Date(row.ts_start);
    const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`;
    if (!seriesMap[dateKey]) return;
    const bucket = classifyApp(row.app_name, row.window_title);
    seriesMap[dateKey][bucket] += durationBetween(row.ts_start, row.ts_end) / 3600;
  });

  return series.map((item) => ({
    ...item,
    productive: Number(item.productive.toFixed(1)),
    unproductive: Number(item.unproductive.toFixed(1)),
    neutral: Number(item.neutral.toFixed(1)),
  }));
}

function buildTopApps(activityRows = []) {
  const appMap = {};

  activityRows.forEach((row) => {
    const name = row.app_name || 'Unknown';
    appMap[name] = (appMap[name] || 0) + durationBetween(row.ts_start, row.ts_end);
  });

  return Object.entries(appMap)
    .map(([name, sec]) => ({ name, hours: Number((sec / 3600).toFixed(1)) }))
    .sort((first, second) => second.hours - first.hours)
    .slice(0, 10);
}

function buildWebCategories(webRows = [], activityRows = []) {
  const categoryMap = {};

  webRows.forEach((row) => {
    const category = categorizeDomain(row.domain);
    categoryMap[category] = (categoryMap[category] || 0) + durationBetween(row.ts_start, row.ts_end);
  });

  // Fallback: Use browser activity windows if direct web events are missing
  if (Object.keys(categoryMap).length === 0) {
    activityRows.forEach((row) => {
      const app = (row.app_name || '').toLowerCase();
      if (app.includes('chrome') || app.includes('edge') || app.includes('firefox')) {
        const domain = extractDomain(row.window_title);
        if (domain !== 'Other') {
          const category = categorizeDomain(domain);
          categoryMap[category] = (categoryMap[category] || 0) + durationBetween(row.ts_start, row.ts_end);
        }
      }
    });
  }

  return Object.entries(categoryMap)
    .map(([name, sec]) => ({ name, value: sec }))
    .sort((first, second) => second.value - first.value);
}

function buildTimeline(activityRows = []) {
  const points = Array.from({ length: 24 }).map((_, hour) => ({
    hour: `${String(hour).padStart(2, '0')}:00`,
    intensity: 0,
  }));

  activityRows.forEach((row) => {
    const date = new Date(row.ts_start);
    const hour = date.getHours();
    points[hour].intensity += durationBetween(row.ts_start, row.ts_end) / 3600;
  });

  return points.map((point) => ({
    ...point,
    intensity: Number(point.intensity.toFixed(1)),
  }));
}

function buildDetailRows(activityRows = [], webRows = []) {
  const activityItems = activityRows.map((row) => ({
    id: `activity-${row.id}`,
    employee: row.employee_name || 'Unknown',
    item: row.window_title || row.app_name || row.process_name || 'Activity event',
    category: classifyApp(row.app_name, row.window_title),
    duration: durationBetween(row.ts_start, row.ts_end),
    startTime: row.ts_start,
  }));

  const webItems = webRows.map((row) => ({
    id: `web-${row.id}`,
    employee: row.employee_name || 'Unknown',
    item: row.url || row.domain || 'Web event',
    category: categorizeDomain(row.domain),
    duration: durationBetween(row.ts_start, row.ts_end),
    startTime: row.ts_start,
  }));

  return [...activityItems, ...webItems].sort(
    (first, second) => new Date(second.startTime).getTime() - new Date(first.startTime).getTime()
  );
}

export default function ProductivityAnalytics() {
  const [range, setRange] = useState('7d');
  const [employeeId, setEmployeeId] = useState('all');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tablePage, setTablePage] = useState(1);
  const [data, setData] = useState({
    summary: null,
    activityRows: [],
    webRows: [],
  });

  useEffect(() => {
    setTablePage(1);
  }, [range, employeeId]);

  useEffect(() => {
    const load = async () => {
      const { startDate, endDate } = getDateWindow(range);
      setLoading(true);

      const [summaryResult, activityResult, webResult, employeesResult] = await Promise.allSettled([
        analyticsApi.productivity({
          start_date: startDate,
          end_date: endDate,
          ...(employeeId !== 'all' ? { employee_id: employeeId } : {}),
        }),
        api.get('/export/activity', {
          params: {
            date_from: startDate,
            date_to: endDate,
            page: 1,
            page_size: 1000,
            ...(employeeId !== 'all' ? { employee_id: employeeId } : {}),
          },
        }),
        api.get('/export/web', {
          params: {
            date_from: startDate,
            date_to: endDate,
            page: 1,
            page_size: 1000,
            ...(employeeId !== 'all' ? { employee_id: employeeId } : {}),
          },
        }),
        api.get('/employees', { params: { page: 1, page_size: 200 } }),
      ]);

      if (summaryResult.status === 'rejected') {
        toast.error('Failed to load productivity analytics.');
      }

      if (activityResult.status === 'rejected' || webResult.status === 'rejected') {
        toast.error('Detailed activity exports are unavailable for this account.');
      }

      const activityItems =
        activityResult.status === 'fulfilled' ? activityResult.value.data?.items || [] : [];
      const webItems = webResult.status === 'fulfilled' ? webResult.value.data?.items || [] : [];

      const filteredActivity =
        employeeId === 'all'
          ? activityItems
          : activityItems.filter(
              (row) => String(row.employee_id).toLowerCase() === String(employeeId).toLowerCase()
            );
      const filteredWeb =
        employeeId === 'all'
          ? webItems
          : webItems.filter(
              (row) => String(row.employee_id).toLowerCase() === String(employeeId).toLowerCase()
            );

      setEmployees(
        employeesResult.status === 'fulfilled' ? employeesResult.value.data?.items || [] : []
      );
      setData({
        summary: summaryResult.status === 'fulfilled' ? summaryResult.value.data : null,
        activityRows: filteredActivity,
        webRows: filteredWeb,
      });
      setLoading(false);
    };

    load();
  }, [range, employeeId]);

  const { days } = getDateWindow(range);
  const topApps = buildTopApps(data.activityRows);
  const webCategories = buildWebCategories(data.webRows);
  const dailyActivity = buildDailyActivity(data.activityRows, days);
  const timeline = buildTimeline(data.activityRows);
  const detailRows = buildDetailRows(data.activityRows, data.webRows);
  const pageSize = 10;
  const paginatedRows = detailRows.slice((tablePage - 1) * pageSize, tablePage * pageSize);
  const mostUsedApp = topApps[0]?.name || 'No data';
  const productiveSeconds = data.activityRows.reduce((sum, row) => {
    if (classifyApp(row.app_name, row.window_title) !== 'productive') return sum;
    return sum + durationBetween(row.ts_start, row.ts_end);
  }, 0);
  const unproductiveSeconds = data.activityRows.reduce((sum, row) => {
    if (classifyApp(row.app_name, row.window_title) !== 'unproductive') return sum;
    return sum + durationBetween(row.ts_start, row.ts_end);
  }, 0);
  const totalWebSeconds = data.webRows.reduce(
    (sum, row) => sum + durationBetween(row.ts_start, row.ts_end),
    0
  );

  return (
    <div className="min-h-screen space-y-6 bg-slate-900 text-white" data-testid="productivity-page">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <p className="text-sm uppercase tracking-[0.35em] text-slate-500">Analytics</p>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight text-white">
            Productivity Analytics
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">
            Application usage, browsing patterns, and activity distribution across the selected time
            window.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="inline-flex rounded-xl border border-slate-700 bg-slate-800/80 p-1">
            {RANGE_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`rounded-lg px-4 py-2 text-sm transition ${
                  range === option.key
                    ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/20'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
                onClick={() => setRange(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <Select value={employeeId} onValueChange={setEmployeeId}>
            <SelectTrigger className="w-[220px] border-slate-700 bg-slate-800 text-slate-200">
              <SelectValue placeholder="All Employees" />
            </SelectTrigger>
            <SelectContent className="border-slate-700 bg-slate-800 text-slate-200">
              <SelectItem value="all">All Employees</SelectItem>
              {employees.map((employee) => (
                <SelectItem key={employee.id} value={employee.id}>
                  {employee.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <>
          <LoadingSkeleton type="card" />
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <LoadingSkeleton type="chart" />
            <LoadingSkeleton type="chart" />
            <LoadingSkeleton type="chart" />
            <LoadingSkeleton type="chart" />
          </div>
          <LoadingSkeleton type="table" />
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <StatCard
              title="Avg Active Hours/Day"
              value={`${((data.summary?.active_sec || 0) / Math.max(days, 1) / 3600).toFixed(1)}h`}
              change={days ? ((data.summary?.active_sec || 0) / Math.max(days, 1) / 3600) * 10 : 0}
              changeLabel="activity cadence"
              icon={<Clock className="h-5 w-5" />}
              color="blue"
            />
            <StatCard
              title="Most Used App"
              value={mostUsedApp}
              change={topApps[0]?.hours || 0}
              changeLabel="hours tracked"
              icon={<Monitor className="h-5 w-5" />}
              color="green"
            />
            <StatCard
              title="Productive Ratio"
              value={`${(productiveSeconds / Math.max(unproductiveSeconds || 1, 1)).toFixed(1)}x`}
              change={
                productiveSeconds + unproductiveSeconds
                  ? (productiveSeconds / (productiveSeconds + unproductiveSeconds)) * 100
                  : 0
              }
              changeLabel="productive share"
              icon={<TrendingUp className="h-5 w-5" />}
              color="purple"
            />
            <StatCard
              title="Total Web Time"
              value={formatDuration(totalWebSeconds)}
              change={totalWebSeconds / 3600}
              changeLabel="browser activity"
              icon={<Globe className="h-5 w-5" />}
              color="yellow"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <Card className="border-slate-700 bg-slate-800 shadow-lg">
              <CardHeader className="pb-0">
                <CardTitle className="text-white">Daily Activity</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {dailyActivity.some((item) => item.productive || item.unproductive || item.neutral) ? (
                  <div className="h-[320px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={dailyActivity}>
                        <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
                        <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                        <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Bar dataKey="productive" stackId="activity" fill={CHART_COLORS.productive} />
                        <Bar dataKey="unproductive" stackId="activity" fill={CHART_COLORS.unproductive} />
                        <Bar dataKey="neutral" stackId="activity" fill={CHART_COLORS.neutral} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <EmptyState
                    icon={<Clock className="h-8 w-8" />}
                    title="No daily activity"
                    description="Activity event detail is unavailable for the selected range."
                  />
                )}
              </CardContent>
            </Card>

            <Card id="web" className="border-slate-700 bg-slate-800 shadow-lg">
              <CardHeader className="pb-0">
                <CardTitle className="text-white">Web Activity (Top Domains)</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {webCategories.length ? (
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr),220px] lg:items-center">
                    <div className="h-[320px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={webCategories}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={70}
                            outerRadius={110}
                            paddingAngle={4}
                          >
                            {webCategories.map((entry, index) => (
                              <Cell
                                key={entry.name}
                                fill={CHART_COLORS.web[index % CHART_COLORS.web.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={tooltipStyle}
                            formatter={(value) => formatDuration(value)}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-3">
                      {webCategories.map((entry, index) => (
                        <div key={entry.name} className="rounded-xl border border-slate-700 bg-slate-900/70 p-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <span
                                className="h-3 w-3 rounded-full"
                                style={{ backgroundColor: CHART_COLORS.web[index % CHART_COLORS.web.length] }}
                              />
                              <span className="text-sm text-slate-200">{entry.name}</span>
                            </div>
                            <span className="text-sm text-slate-400">{formatDuration(entry.value)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <EmptyState
                    icon={<Globe className="h-8 w-8" />}
                    title="No web data"
                    description="Web activity export is empty for the selected filters."
                  />
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-700 bg-slate-800 shadow-lg">
              <CardHeader className="pb-0">
                <CardTitle className="text-white">Top Applications</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {topApps.length ? (
                  <div className="h-[320px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={topApps} layout="vertical" margin={{ left: 10, right: 20 }}>
                        <CartesianGrid stroke="#334155" horizontal={false} />
                        <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={110}
                          tick={{ fill: '#94a3b8', fontSize: 12 }}
                        />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Bar dataKey="hours" radius={[0, 10, 10, 0]}>
                          {topApps.map((app, index) => (
                            <Cell
                              key={app.name}
                              fill={CHART_COLORS.apps[index % CHART_COLORS.apps.length]}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <EmptyState
                    icon={<Monitor className="h-8 w-8" />}
                    title="No application data"
                    description="No activity events were exported for this slice."
                  />
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-700 bg-slate-800 shadow-lg">
              <CardHeader className="pb-0">
                <CardTitle className="text-white">Activity Timeline</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {timeline.some((point) => point.intensity > 0) ? (
                  <div className="h-[320px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={timeline}>
                        <defs>
                          <linearGradient id="productivityArea" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.45} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
                        <XAxis dataKey="hour" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                        <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Area
                          type="monotone"
                          dataKey="intensity"
                          stroke="#3b82f6"
                          fill="url(#productivityArea)"
                          strokeWidth={2.5}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <EmptyState
                    icon={<TrendingUp className="h-8 w-8" />}
                    title="No timeline data"
                    description="Hourly intensity becomes available when activity exports are enabled."
                  />
                )}
              </CardContent>
            </Card>
          </div>


          <Card className="border-slate-700 bg-slate-800 shadow-lg">
            <CardHeader className="pb-0">
              <CardTitle className="text-white">Detailed Activity Logs</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <DataTable
                columns={[
                  { key: 'employee', label: 'Employee' },
                  {
                    key: 'item',
                    label: 'App / URL',
                    render: (value) => (
                      <span className="line-clamp-1 max-w-[420px] text-slate-200">{value}</span>
                    ),
                  },
                  {
                    key: 'category',
                    label: 'Category',
                    render: (value) => <span className="capitalize">{value}</span>,
                  },
                  {
                    key: 'duration',
                    label: 'Duration',
                    render: (value) => formatDuration(value),
                  },
                  {
                    key: 'startTime',
                    label: 'Start Time',
                    render: (value) => formatTimestamp(value),
                  },
                ]}
                data={paginatedRows}
                loading={loading}
                emptyMessage="No detailed activity records are available for this range."
                pagination={{
                  page: tablePage,
                  pageSize,
                  total: detailRows.length,
                  onPageChange: setTablePage,
                }}
              />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
